var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../services/api-client';
import './jv-camera-tile';
import './jv-quota-meter';
let JvDashboard = class JvDashboard extends LitElement {
    constructor() {
        super(...arguments);
        this._cameras = [];
        this._quota = null;
        this._loading = true;
        this._error = '';
    }
    static { this.styles = css `
    :host {
      display: block;
      min-height: 100vh;
    }
    .topbar {
      background: #3A3530;
      color: #fff;
    }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._loadData();
    }
    async _loadData() {
        this._loading = true;
        try {
            const [cameras, quota] = await Promise.all([
                api.get('/api/cameras/assigned'),
                api.get('/api/quotas/me'),
            ]);
            this._cameras = cameras;
            this._quota = quota;
        }
        catch (err) {
            const apiErr = err;
            this._error = apiErr?.error ?? 'Failed to load data';
        }
        finally {
            this._loading = false;
        }
    }
    _handleLogout() {
        this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }));
    }
    _handleViewCamera(e) {
        const { cameraId } = e.detail;
        this.dispatchEvent(new CustomEvent('navigate', {
            detail: { route: 'live', cameraId },
            bubbles: true,
            composed: true,
        }));
    }
    _openParentPage(page) {
        this.dispatchEvent(new CustomEvent('navigate', {
            detail: { route: 'parent-page', page },
            bubbles: true,
            composed: true,
        }));
    }
    _formatMinutes(m) {
        const h = Math.floor(m / 60);
        const mins = m % 60;
        if (h > 0 && mins > 0)
            return `${h}h ${mins}m`;
        if (h > 0)
            return `${h}h`;
        return `${mins}m`;
    }
    render() {
        const profile = this.profile;
        if (!profile)
            return html ``;
        return html `
      <header class="topbar w-full">
        <div class="max-w-content mx-auto px-5 sm:px-8 h-14 flex items-center gap-4">
          <span class="inline-flex items-center gap-2">
            <span class="grid place-items-center w-7 h-7 rounded-lg bg-jv-primary text-white">
              <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
            </span>
            <span class="font-bold text-sm tracking-tight">Just<span class="text-jv-primary">Vue</span></span>
          </span>
          <span class="ml-auto text-xs text-white/70">${profile.displayName}</span>
          <button @click=${this._handleLogout} class="text-xs text-white/70 hover:text-white font-medium transition">Sign out</button>
        </div>
      </header>

      <main class="min-h-[calc(100vh-56px)]">
        <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
          <div class="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <h1 class="text-2xl font-extrabold tracking-tight text-jv-ink">Hi, ${profile.displayName.split(' ')[0]}</h1>
              <p class="text-sm text-jv-ink-2">Here are your cameras</p>
            </div>
            <nav aria-label="Parent account" class="ml-auto flex flex-wrap gap-2">
              ${[['grid', 'All cameras'], ['out-of-hours', 'Viewing hours'], ['quota-exceeded', 'Quota'], ['history', 'History'], ['account', 'Account'], ['recordings', 'Recordings']].map(([page, label]) => html `
                <button class="jv-btn-ghost" @click=${() => this._openParentPage(page)}>${label}</button>
              `)}
            </nav>
          </div>

          ${this._error ? html `
            <div class="jv-card mb-6">
              <div class="p-4 bg-jv-danger-050 text-jv-danger text-sm font-medium rounded-t-2xl">
                ${this._error}
                <button @click=${this._loadData} class="ml-3 underline font-semibold">Retry</button>
              </div>
            </div>
          ` : ''}

          ${this._loading ? html `
            <div class="flex items-center justify-center py-20 text-jv-ink-2 text-sm">Loading your cameras…</div>
          ` : html `
            <div class="grid lg:grid-cols-3 gap-6">
              <div class="lg:col-span-2 space-y-4">
                <div class="flex items-center justify-between">
                  <h2 class="text-sm font-semibold uppercase tracking-wider text-jv-ink-2">Your cameras</h2>
                  <span class="text-xs text-jv-ink-2">${this._cameras.length} camera${this._cameras.length !== 1 ? 's' : ''}</span>
                </div>
                ${this._cameras.length === 0 ? html `
                  <div class="jv-card">
                    <div class="p-10 text-center">
                      <p class="font-semibold text-jv-ink">No cameras assigned yet</p>
                      <p class="text-sm text-jv-ink-2 mt-1">Your school will assign cameras to your account.</p>
                    </div>
                  </div>
                ` : html `
                  <div class="grid sm:grid-cols-2 gap-5">
                    ${this._cameras.map(c => html `
                      <jv-camera-tile .camera=${c} @view-camera=${this._handleViewCamera}></jv-camera-tile>
                    `)}
                  </div>
                `}
              </div>

              <div class="space-y-5">
                ${this._quota ? html `
                  <section class="jv-card">
                    <div class="jv-card-body">
                      <h3 class="font-bold text-[15px] text-jv-ink mb-1">Your month</h3>
                      <p class="text-sm text-jv-ink-2 mb-4">
                        ${this._formatMinutes(this._quota.remainingMinutes)} to go until reset.
                      </p>
                      <jv-quota-meter used=${this._quota.usedMinutes} total=${this._quota.totalMinutes}></jv-quota-meter>
                      <p class="text-xs text-jv-ink-2 mt-4 flex items-start gap-1.5">
                        <svg viewBox="0 0 24 24" class="w-[14px] h-[14px] mt-px shrink-0" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        Your monthly viewing time is managed by JustVue for every family — you'll never be charged for going over.
                      </p>
                    </div>
                  </section>
                ` : ''}

                <section class="jv-card">
                  <div class="jv-card-body flex items-start gap-3">
                    <span class="grid place-items-center w-10 h-10 rounded-xl bg-jv-primary-050 text-jv-primary shrink-0">
                      <svg viewBox="0 0 24 24" class="w-[19px] h-[19px]" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    </span>
                    <div class="text-sm leading-snug">
                      <p class="font-bold text-jv-ink">Watched with care</p>
                      <p class="text-jv-ink-2 mt-0.5">Every time a camera is opened it's logged, so your school always knows who watched and when.</p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          `}
        </div>
      </main>
    `;
    }
};
__decorate([
    property({ type: Object })
], JvDashboard.prototype, "profile", void 0);
__decorate([
    state()
], JvDashboard.prototype, "_cameras", void 0);
__decorate([
    state()
], JvDashboard.prototype, "_quota", void 0);
__decorate([
    state()
], JvDashboard.prototype, "_loading", void 0);
__decorate([
    state()
], JvDashboard.prototype, "_error", void 0);
JvDashboard = __decorate([
    customElement('jv-dashboard')
], JvDashboard);
export { JvDashboard };
